import { WebSocketSubject, webSocket } from 'rxjs/webSocket'
import { retry, skipUntil, Subscription, takeUntil, timer } from 'rxjs'
import { Injectable } from '@angular/core'
import { LogData, LogService } from 'htv-lib'
import { environment } from '../environments/environment'
import { HttpUtil } from '../utils/http-util'
import { LogStartOptions, SocketAction, SocketClientDto, SocketServerDto } from '../interfaces/websocket'
import { StateDataService } from './state-data.service'

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket$?: WebSocketSubject<any>
  private socketSubscription?: Subscription

  logStreamEndAt?: Date
  logStreamEnabled = false
  private logStreamSubscription?: Subscription

  private readonly retryDelay = 10000

  constructor(
    private logService: LogService,
    private stateDataService: StateDataService
  ) {
    // do nothing
  }

  init(): void {
    this.logService.info(`[WebSocket] Init`)

    this.stateDataService.deviceId$.subscribe((deviceId?: string) => {
      if (!deviceId) {
        this.logService.error(`[WebSocket] Device is not yet registered`)
      } else {
        this.createWebSocketClient()
        this.registerSocketListener()
      }
    })
  }

  createWebSocketClient(): void {
    const user = this.stateDataService.deviceId
    if (!user) {
      this.logService.error(`[WebSocket] Device is not yet registered`)
      return
    }
    const token = HttpUtil.getHTVAuthToken(user)
    const serverUrl = `${environment.deviceWebSocketUrl}/${token}`
    this.socket$ = webSocket({
      url: serverUrl,
      openObserver: {
        next: () => this.logService.info('[WebSocket] Connected to server'),
      },
      closeObserver: {
        next: () => this.logService.error('[WebSocket] Connection closed'),
      },
      closingObserver: {
        next: () => this.logService.info('[WebSocket] Closing connection'),
      },
    })
  }

  registerSocketListener(): void {
    if (!this.socket$) {
      this.logService.error(`[WebSocket] WebSocket is not connected`)
      return
    }
    if (this.socketSubscription) {
      this.socketSubscription.unsubscribe()
    }
    this.socketSubscription = this.socket$.pipe(retry({ delay: this.retryDelay })).subscribe({
      next: (data: SocketServerDto) => {
        try {
          this.logService.log(`[WebSocket] Message received: ${JSON.stringify(data)}`)
          this.handleServerMessage(data)
        } catch (err: any) {
          this.logService.error(err)
        }
      },
      error: e => {
        this.logService.error('[WebSocket] ' + e?.message)
      },
      complete: () => {
        this.logService.info('[WebSocket] Connection complete')
      },
    })
  }

  handleServerMessage(data: SocketServerDto): void {
    switch (data?.action) {
      case SocketAction.LOG_START: {
        const options = data.options as LogStartOptions
        const withReplay = options.withReplay ?? false
        this.startLogForwarding(options.endAt, withReplay)
        break
      }
      case SocketAction.LOG_STOP: {
        this.stopLogForwarding()
        break
      }
      default:
        this.logService.error('[WebSocket] Unknown action: ' + data?.action)
        break
    }
  }

  startLogForwarding(endAt: Date, withReplay: boolean): void {
    if (!this.socket$) return

    this.stopLogForwarding()

    const endAtDate = new Date(endAt)
    const msUntilDate = endAtDate.getTime() - Date.now()
    if (msUntilDate <= 0) {
      this.logService.info('[WebSocket] Log stream end date already passed')
      return
    }

    this.logStreamEndAt = endAt
    this.logStreamEnabled = true

    const obs$ = withReplay
      ? this.logService.logs$.pipe(takeUntil(timer(msUntilDate)))
      : this.logService.logs$.pipe(skipUntil(timer(0)), takeUntil(timer(msUntilDate)))
    this.logStreamSubscription = obs$.subscribe({
      next: (log: LogData) => {
        if (this.socket$) {
          const data: SocketClientDto = {
            ts: log.ts,
            level: log.level,
            message: log.message,
          }
          this.socket$.next(data)
        }
      },
      complete: () => {
        if (this.socket$) {
          const data: SocketClientDto = {
            ts: new Date(),
            level: 'info',
            message: '[WebSocket] Closing log stream',
          }
          this.socket$.next(data)
        }
        this.stopLogForwarding()
      },
    })
  }

  stopLogForwarding(): void {
    this.logStreamEndAt = undefined
    this.logStreamEnabled = false
    if (this.logStreamSubscription) {
      this.logStreamSubscription.unsubscribe()
    }
    this.logStreamSubscription = undefined
  }
}
